import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { emptyScenarioInput } from '@exigo/shared';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/Button';
import { TextInput } from '../../components/ui/TextInput';
import { Select } from '../../components/ui/Select';
import { Tabs } from '../../components/ui/Tabs';
import { EmptyState } from '../../components/ui/EmptyState';
import { Spinner } from '../../components/ui/Spinner';
import { useAuth } from '../../auth/AuthProvider';
import { useReferenceQuery } from '../../api/queries';
import { useScenarios } from '../../lib/useScenarios';
import type { ScenarioRecord } from '../../lib/scenarioStore';
import { ScenarioCard } from './ScenarioCard';
import styles from './ScenarioListPage.module.css';

type StatusFilter = 'all' | 'draft' | 'finalized';

export function ScenarioListPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { user } = useAuth();
  const reference = useReferenceQuery();
  const { scenarios, isLoading: scenariosLoading, create, remove } = useScenarios();

  const [search, setSearch] = useState('');
  const [areaFilter, setAreaFilter] = useState('all');
  const [status, setStatus] = useState<StatusFilter>('all');

  const areaLabelByKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of reference.data?.areas ?? []) map.set(a.key, a.name);
    return map;
  }, [reference.data]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return scenarios.filter((s) => {
      if (status !== 'all' && s.status !== status) return false;
      if (areaFilter !== 'all' && s.input.area !== areaFilter) return false;
      if (!term) return true;
      return (
        s.name.toLowerCase().includes(term) ||
        (s.customerName?.toLowerCase().includes(term) ?? false) ||
        (areaLabelByKey.get(s.input.area)?.toLowerCase().includes(term) ?? false)
      );
    });
  }, [scenarios, status, areaFilter, search, areaLabelByKey]);

  async function handleCreate(): Promise<void> {
    if (!reference.data) return;
    const record = await create({
      name: 'Untitled scenario',
      customerName: null,
      opportunityRef: null,
      guideVersion: reference.data.guideVersion,
      input: emptyScenarioInput({
        specs: reference.data.defaults.specs,
        designations: reference.data.defaults.designations,
        workloads: reference.data.defaults.workloads,
      }),
      promoInput: null,
      promoCatalogue: null,
      status: 'draft',
      ownerName: user?.displayName ?? user?.upn ?? null,
      summary: null,
    });
    void navigate(`/scenarios/${record.id}`);
  }

  async function handleDuplicate(record: ScenarioRecord): Promise<void> {
    const copy = await create({
      ...record,
      name: `${record.name} (Copy)`,
      status: 'draft',
      summary: null,
    });
    void navigate(`/scenarios/${copy.id}`);
  }

  const areaOptions = [
    { value: 'all', label: 'All areas' },
    ...(reference.data?.areas.map((a) => ({ value: a.key, label: a.name })) ?? []),
  ];

  const counts = {
    all: scenarios.length,
    draft: scenarios.filter((s) => s.status === 'draft').length,
    finalized: scenarios.filter((s) => s.status === 'finalized').length,
  };

  return (
    <>
      <PageHeader
        title="MCI Engagement Finder"
        meta={`${String(scenarios.length)} scenario${scenarios.length === 1 ? '' : 's'}`}
        actions={
          <>
            <div className={styles.search}>
              <SearchIcon />
              <TextInput
                placeholder="Search by name, customer, or area..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); }}
                aria-label="Search scenarios"
              />
            </div>
            <div className={styles.areaSelect}>
              <Select value={areaFilter} onChange={setAreaFilter} options={areaOptions} ariaLabel="Filter by area" />
            </div>
            <Button variant="primary" onClick={() => { void handleCreate(); }} disabled={!reference.data}>
              <PlusIcon /> New scenario
            </Button>
          </>
        }
        tabs={
          <Tabs
            value={status}
            onChange={(v) => { setStatus(v as StatusFilter); }}
            ariaLabel="Filter by status"
            items={[
              { value: 'all', label: 'All', count: counts.all },
              { value: 'draft', label: 'Draft', count: counts.draft },
              { value: 'finalized', label: 'Finalized', count: counts.finalized },
            ]}
          />
        }
      />

      <div className={styles.body}>
        {reference.isLoading || scenariosLoading ? (
          <div className={styles.loading}>
            <Spinner size={22} />
          </div>
        ) : reference.isError ? (
          <EmptyState title="Could not load reference data" body="Check the API is running and try again." />
        ) : filtered.length === 0 && scenarios.length === 0 ? (
          <EmptyState
            title="No scenarios yet"
            body="Create your first scenario to work out which MCI engagements a deal can claim."
            action={
              <Button variant="primary" onClick={() => { void handleCreate(); }}>
                <PlusIcon /> New scenario
              </Button>
            }
          />
        ) : filtered.length === 0 ? (
          <EmptyState title="No scenarios match" body="Try a different search term or filter." />
        ) : (
          <div className={styles.grid}>
            {filtered.map((record) => (
              <ScenarioCard
                key={record.id}
                record={record}
                areaLabel={areaLabelByKey.get(record.input.area) ?? record.input.area}
                onDuplicate={(r) => { void handleDuplicate(r); }}
                onDelete={remove}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function SearchIcon(): React.JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className={styles.searchIcon}>
      <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M13.5 13.5L10.5 10.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function PlusIcon(): React.JSX.Element {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
      <path d="M8 2.5V13.5M2.5 8H13.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
